/**
 * Dependency-free carousel, replacing jQuery + Slick.
 *
 * Uses native scroll-snap for the movement itself, so touch/trackpad flicking,
 * momentum and accessibility come from the browser. JS only adds the dots,
 * wires optional external arrows, and keeps their state in sync.
 *
 * Markup:
 *   <simple-carousel data-per-view="4" data-per-view-tablet="3" data-per-view-mobile="1"
 *                    data-dots="true" data-prev=".custom-prev" data-next=".custom-next">
 *     <div class="track">        <- first element child becomes the track
 *       <div>slide</div>
 *     </div>
 *   </simple-carousel>
 */
class SimpleCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.firstElementChild;
    if (!this.track) return;

    this.slides = Array.from(this.track.children);
    if (this.slides.length === 0) return;

    this.track.classList.add('simple-carousel__track');
    this.slides.forEach((slide) => slide.classList.add('simple-carousel__slide'));

    this.applyPerView();
    this.buildDots();
    this.bindArrows();

    this.onScroll = this.debounce(() => this.syncState(), 80);
    this.track.addEventListener('scroll', this.onScroll, { passive: true });

    this.onResize = this.debounce(() => {
      this.applyPerView();
      this.buildDots();
      this.syncState();
    }, 150);
    window.addEventListener('resize', this.onResize);

    this.syncState();
  }

  disconnectedCallback() {
    this.track?.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('resize', this.onResize);
  }

  debounce(fn, wait) {
    let timer;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => fn.apply(this, args), wait);
    };
  }

  get perView() {
    const width = window.innerWidth;
    const pick =
      width < 768
        ? this.dataset.perViewMobile
        : width < 1024
        ? this.dataset.perViewTablet
        : this.dataset.perView;

    return parseFloat(pick || this.dataset.perView || 1) || 1;
  }

  applyPerView() {
    this.style.setProperty('--simple-carousel-per-view', this.perView);
  }

  /** Number of distinct scroll positions, scrolling one slide at a time. */
  get pageCount() {
    return Math.max(1, this.slides.length - Math.floor(this.perView) + 1);
  }

  get currentIndex() {
    const slideWidth = this.slides[0]?.getBoundingClientRect().width || 1;
    return Math.round(this.track.scrollLeft / slideWidth);
  }

  buildDots() {
    if (this.dataset.dots !== 'true') return;

    if (!this.dotsContainer) {
      this.dotsContainer = document.createElement('ul');
      this.dotsContainer.className = 'simple-carousel__dots';
      this.dotsContainer.setAttribute('role', 'list');
      this.appendChild(this.dotsContainer);
    }

    const count = this.pageCount;
    if (this.dotsContainer.children.length === count) return;

    this.dotsContainer.innerHTML = '';
    for (let i = 0; i < count; i += 1) {
      const li = document.createElement('li');
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'simple-carousel__dot';
      button.setAttribute('aria-label', `Go to slide ${i + 1}`);
      button.addEventListener('click', () => this.goTo(i));
      li.appendChild(button);
      this.dotsContainer.appendChild(li);
    }
  }

  bindArrows() {
    const scope = this.closest('section') || document;
    this.prevButton = this.dataset.prev ? scope.querySelector(this.dataset.prev) : null;
    this.nextButton = this.dataset.next ? scope.querySelector(this.dataset.next) : null;

    this.prevButton?.addEventListener('click', () => this.goTo(this.currentIndex - 1));
    this.nextButton?.addEventListener('click', () => this.goTo(this.currentIndex + 1));
  }

  goTo(index) {
    const clamped = Math.max(0, Math.min(index, this.pageCount - 1));
    const slideWidth = this.slides[0]?.getBoundingClientRect().width || 0;
    this.track.scrollTo({ left: clamped * slideWidth, behavior: 'smooth' });
  }

  syncState() {
    const index = this.currentIndex;

    if (this.dotsContainer) {
      Array.from(this.dotsContainer.children).forEach((li, i) => {
        const dot = li.firstElementChild;
        dot?.classList.toggle('is-active', i === index);
        if (i === index) {
          dot?.setAttribute('aria-current', 'true');
        } else {
          dot?.removeAttribute('aria-current');
        }
      });
    }

    // Non-infinite, matching the previous Slick config.
    if (this.prevButton) this.prevButton.disabled = index <= 0;
    if (this.nextButton) this.nextButton.disabled = index >= this.pageCount - 1;
  }
}

customElements.define('simple-carousel', SimpleCarousel);
